import axios from "axios";
import {
  API_BASE_URL,
  CLIENT_NAME_HEADER_KEY,
  CLIENT_NAME_HEADER_VALUE,
  O_CLIENT_APP,
  O_API_KEY,
} from "constants";
import tokenRefreshService from "./tokenRefreshService";
import paths from "routes/paths";
import {
  getCurrentAppPath,
  savePostLoginRedirect,
} from "utils/postLoginRedirect";
import { clearClientData, ensureClientId } from "utils/clientBootstrap";

const FILE_IDS_NOT_FOUND_PATTERN = /files?\s+with\s+fileids?.*not found/i;
const FILES_NOT_READY_MESSAGE =
  "Some uploaded files are not available yet. Please wait a few seconds and try again.";
const GLOBAL_SELECTED_WORKSPACE_KEY = "selectedWorkspaceId";
const GLOBAL_SELECTED_WORKSPACE_READY_KEY = "selectedWorkspaceReady";
const WORKSPACE_QUERY_PARAM = "wid";
const LEGACY_WORKSPACE_QUERY_PARAM = "workspaceId";
const SKIP_WORKSPACE_CONTEXT_HEADER = "O-SKIP-WORKSPACE-CONTEXT";
const WORKSPACE_ID_HEADER = "O-WORKSPACE-ID";
const WORKSPACE_WAIT_TIMEOUT_MS = 1800;
const WORKSPACE_WAIT_INTERVAL_MS = 50;

const parseErrorPayload = (error) => {
  const status = error?.response?.status;
  const data = error?.response?.data;

  if (data && typeof data === "object") {
    return {
      status: status ?? data.code,
      message: data.message || data.error || null,
    };
  }

  if (typeof data === "string") {
    try {
      const parsed = JSON.parse(data);
      if (parsed && typeof parsed === "object") {
        return {
          status: status ?? parsed.code,
          message: parsed.message || parsed.error || data,
        };
      }
    } catch {
      return { status, message: data };
    }
    return { status, message: data };
  }

  return { status, message: null };
};

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
    "O-API-KEY": O_API_KEY,
    "O-CLIENT-APP": O_CLIENT_APP,
    // Backend services (e.g. payment-service) expect the lowercase, hyphenated app string.
    [CLIENT_NAME_HEADER_KEY]: CLIENT_NAME_HEADER_VALUE,
    "X-API-KEY": O_API_KEY, // for Projects Provider
  }
});

const selectedWorkspaceId = () => {
  const direct = localStorage.getItem(GLOBAL_SELECTED_WORKSPACE_KEY);
  if (direct) return direct;

  for (let index = 0; index < localStorage.length; index += 1) {
    const key = localStorage.key(index);
    if (key?.startsWith("selectedWorkspaceId:")) {
      const value = localStorage.getItem(key);
      if (value) return value;
    }
  }

  return "";
};

const workspaceSelectionReady = () =>
  localStorage.getItem(GLOBAL_SELECTED_WORKSPACE_READY_KEY) === "1";

const waitForWorkspaceSelection = async () => {
  if (selectedWorkspaceId() || workspaceSelectionReady()) {
    return selectedWorkspaceId();
  }

  const startedAt = Date.now();
  while (Date.now() - startedAt < WORKSPACE_WAIT_TIMEOUT_MS) {
    await new Promise((resolve) => setTimeout(resolve, WORKSPACE_WAIT_INTERVAL_MS));
    const workspaceId = selectedWorkspaceId();
    if (workspaceId || workspaceSelectionReady()) {
      return workspaceId;
    }
  }

  return selectedWorkspaceId();
};

const withWorkspaceParam = (params, workspaceId) => {
  if (!workspaceId) return params;
  if (params instanceof URLSearchParams) {
    const nextParams = new URLSearchParams(params);
    nextParams.set(WORKSPACE_QUERY_PARAM, workspaceId);
    return nextParams;
  }

  const nextParams = {
    ...(params || {}),
    [WORKSPACE_QUERY_PARAM]: workspaceId,
  };

  return nextParams;
};

const appendWorkspaceContext = async (config) => {
  const workspaceId = await waitForWorkspaceSelection();
  if (workspaceId) {
    config.params = withWorkspaceParam(config.params, workspaceId);
    config.headers[WORKSPACE_ID_HEADER] = workspaceId;
  }
};

const prepareRequestConfig = async (config) => {
  const token = sessionStorage.getItem("authToken");
  const clientId = localStorage.getItem("clientId");
  config.headers = config.headers || {};
  const skipWorkspaceContext = Boolean(config.headers[SKIP_WORKSPACE_CONTEXT_HEADER]);
  if (skipWorkspaceContext) {
    delete config.headers[SKIP_WORKSPACE_CONTEXT_HEADER];
  }
  config.headers["O-CLIENT-APP"] = O_CLIENT_APP;
  config.headers[CLIENT_NAME_HEADER_KEY] = CLIENT_NAME_HEADER_VALUE;
  config.headers["O-API-KEY"] = O_API_KEY;
  config.headers["X-API-KEY"] = O_API_KEY;

  const isRegistrationRequest = 
    config.url?.includes('/clients/register/request') ||
    config.url?.includes('/clients/register/confirm');
  
  if (clientId && !isRegistrationRequest) {
    config.headers["O-CLIENT-ID"] = clientId;
  }

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  if (!skipWorkspaceContext) {
    await appendWorkspaceContext(config);
  }

  return config;
};

axios.interceptors.request.use(prepareRequestConfig);
apiClient.interceptors.request.use(prepareRequestConfig);

apiClient.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;
    const { status, message } = parseErrorPayload(error);

    if (message) {
      const normalizedMessage =
        Number(status) === 404 && FILE_IDS_NOT_FOUND_PATTERN.test(message)
          ? FILES_NOT_READY_MESSAGE
          : message;
      error.message = normalizedMessage;
    }

	    if (
	      error.response?.status === 401 &&
	      !original._retry &&
	      sessionStorage.getItem("refreshToken")
	    ) {
	      original._retry = true;

	      try {
	        await tokenRefreshService.refreshTokens();
	        original.headers.Authorization = `Bearer ${sessionStorage.getItem("authToken")}`;
	        return apiClient(original);
	      } catch {
	        savePostLoginRedirect(getCurrentAppPath());
	        window.location.href = paths.simpleSessionExpired;
	      }
	    }

	    // Auto-recover from invalid/unknown client ID — clear stale data and retry
	    if (
	      error.response?.status === 403 &&
	      !original._clientRetry &&
	      message?.toLowerCase().includes("invalid or unknown client id")
	    ) {
	      original._clientRetry = true;
	      localStorage.removeItem("clientId");
	      localStorage.removeItem("clientValidated");

	      try {
	        const newClientId = await ensureClientId();
	        if (newClientId) {
	          original.headers["O-CLIENT-ID"] = newClientId;
	          return apiClient(original);
	        }
	      } catch {
	        // Silently fall through to reject below
	      }
	    }

	    return Promise.reject(error);
  }
);

export default apiClient;
